import { GetServerSideProps } from 'next';
import React, { lazy, Suspense } from 'react';
import { lazily } from 'react-lazily';
import { z } from 'zod';

import { getByUrl } from 'services/freeform';
import { HeadObject } from 'services/generic';
import {
	getArticleById,
	getCategories,
	getRelatedArticles,
	getSectionById,
	zenDeskArticleSchema,
	zenDeskCategorySchema,
} from 'services/helpcentre';

import { useBrowsing } from 'hooks/use-browsing';
import { ReplaceCrumb, useSetBreadcrumbs } from 'hooks/use-set-breadcrumbs';

import { Seo } from 'core/mancity/components/general/seo';
import type { FreeFormBody } from 'core/mancity/template/freeform';

import {
	addFreeformControlData,
	addTrandingRelatedControl,
} from '@help-centre/helpers';

import styles from '../../index.module.scss';

const Breadcrumbs = lazy(
	() => import('core/generic/components/navigation/breadcrumbs')
);
const { FreeformPage } = lazily(() => import('core/mancity/template/freeform'));

type SingleArticlePropsType = {
	body: FreeFormBody;
	currentCategory: z.infer<typeof zenDeskCategorySchema>;
	article: z.infer<typeof zenDeskArticleSchema>;
	metaData: HeadObject;
};

export default function SingleArticle({
	body,
	currentCategory,
	article,
	metaData,
}: SingleArticlePropsType) {
	const router = useBrowsing();
	const { generateSpecificBreadcrumbs } = useSetBreadcrumbs();

	const titleSuffix = ' | Man City Help Centre'; // @totdo: get this from translation dictionary
	metaData.title = article.title + titleSuffix;

	const breadcrubmsReplace: Map<string, ReplaceCrumb> = new Map([
		[
			'/article',
			{
				href: currentCategory.url,
				title: currentCategory.name,
			},
		],
		[`${router.asPath.split('?')[0]}`, { title: article.title }],
	]);

	const specificBreadcrumbs = generateSpecificBreadcrumbs({
		baseName: 'Help & Support',
		replace: breadcrubmsReplace,
	});

	return (
		<>
			{metaData && <Seo head={metaData} />}
			<div>
				<div className="row">
					<div className="col-12">
						<div className={styles.breadcrumbs}>
							<Breadcrumbs listCrumbs={specificBreadcrumbs} />
						</div>
						<Suspense fallback={null}>
							<FreeformPage body={body} />
						</Suspense>
					</div>
				</div>
			</div>
		</>
	);
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const { id } = context.params as { id: string };
	const slugParts = id.split('-');
	const slugId = slugParts[slugParts.length - 1] || id;
	let metaData = null;
	const categories = await getCategories().then((response) => response);

	const article = await getArticleById(+slugId)
		.then((response) => response)
		.catch(() => null);
	if (!article) {
		// If endpoint returns 404 OR 500 OR invalid, show Next.js 404 page
		return { notFound: true };
	}

	const section = await getSectionById(article.sectionId).then(
		(response) => response
	);
	if (!section) {
		return { notFound: true };
	}
	const currentCategory = categories.filter(
		(category) => category.zenDeskId === section.categoryId
	)[0];
	if (!currentCategory) {
		return { notFound: true };
	}

	const relatedArticles = await getRelatedArticles(+slugId).then(
		(response) => response
	);

	const body = await getByUrl('/help/article', 'en', 'gb')
		.then((response) => {
			metaData = response.head;
			return addFreeformControlData(response, 'ZenDeskArticle', article);
		})
		.catch(() => null);
	if (!body || !metaData) {
		return { notFound: true };
	}

	return {
		props: {
			body: addTrandingRelatedControl(
				body,
				'ZenDeskArticle',
				relatedArticles || [],
				'Related articles'
			),
			currentCategory,
			article,
			metaData,
		},
	};
};
